import React, { useState } from "react";
import { Box, Grid, Stack, Typography, Radio } from "@mui/material";
import Button, { Color } from "../../../../../components/Form/Button";
import Input from "../../../../../components/Form/Input";
import { useTranslation } from "react-i18next";
import { cardBoxSx, radioSx, optionTextSx } from "./styles";
import { commonStyles } from "../../tokens";
import Lottie from "lottie-react";
import applauseAnim from "../../../../../assets/img/animations/applause.json";

type DiscoveryKey =
  | "website"
  | "email_marketing"
  | "google"
  | "newsletter"
  | "ai_tools"
  | "referral"
  | "other";

interface FifthStepProps {
  isLoading?: boolean;
  defaultValue?: DiscoveryKey | null;
  companyType?: string;
  onPreviousStep?: () => void;
  onBackToStep?: (step: number) => void;
  onNextStep?: (data: {
    discover: DiscoveryKey;
    otherDetails?: string;
    referralSource?: string;
  }) => void;
}

const OptionRow: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
  "data-cy"?: string;
}> = ({ label, selected, onClick, ...rest }) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
    sx={cardBoxSx(selected)}
    {...rest}
  >
    <Stack direction="row" alignItems="center">
      <Radio checked={selected} readOnly />
      <Typography sx={optionTextSx}>{label}</Typography>
    </Stack>
  </Box>
);

const FifthStep: React.FC<FifthStepProps> = ({
  isLoading,
  defaultValue,
  companyType,
  onPreviousStep,
  onBackToStep,
  onNextStep,
}) => {
  const { t: translate } = useTranslation();
  const [value, setValue] = useState<DiscoveryKey | null>(defaultValue ?? null);
  const [otherDetails, setOtherDetails] = useState<string>("");
  const [referralSource, setReferralSource] = useState<string>("");

  const isValid = value &&
    (value === "other" ? otherDetails.trim().length > 0 : true) &&
    (value === "referral" ? referralSource.trim().length > 0 : true);

  const handleNext = () => {
    if (!value || !isValid) return;
    const data = {
      discover: value,
      ...(value === "other" && { otherDetails: otherDetails.trim() }),
      ...(value === "referral" && { referralSource: referralSource.trim() }),
    };
    onNextStep?.(data);
  };

  const handleBack = () => {
    console.log("companyType", companyType);
    if (companyType === "SP") {
      onBackToStep?.(1);
    } else {
      onPreviousStep?.();
    }
  };

  const options: Array<{ key: DiscoveryKey; tkey: string; cy: string }> = [
    { key: "website",         tkey: "signup.right.discover.website",    cy: "discover-website" },
    { key: "email_marketing", tkey: "signup.right.discover.email",      cy: "discover-email" },
    { key: "google",          tkey: "signup.right.discover.google",     cy: "discover-google" },
    { key: "newsletter",      tkey: "signup.right.discover.newsletter", cy: "discover-newsletter" },
    { key: "ai_tools",        tkey: "signup.right.discover.aiTools",    cy: "discover-ai-tools" },
    { key: "referral",        tkey: "signup.right.discover.referral",   cy: "discover-referral" },
    { key: "other",           tkey: "signup.right.discover.other",      cy: "discover-other" },
  ];

  return (
    <>
      <Grid item xs={12} textAlign="center">
        <Box sx={{ width: 72, height: 72, mx: "auto", mb: 1.5 }}>
          <Lottie animationData={applauseAnim} loop autoplay style={{ width: "100%", height: "100%" }} />
        </Box>

        <Typography
          fontFamily="Manrope"
          fontWeight={700}
          fontSize="28px"
          lineHeight="36px"
          color="#1D2939"
          sx={{ mb: 1.5 }}
        >
          {translate("signup.right.step5Title")}
        </Typography>

        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          lineHeight="24px"
          letterSpacing="0.5px"
          color="#475467"
        >
          {translate("signup.right.step5Subtitle")}
        </Typography>
      </Grid>

      <Grid item xs={12} mt={3}>
        <Stack spacing={1.5}>
          {options.map((opt) => (
            <Box key={opt.key}>
              <OptionRow
                data-cy={opt.cy}
                label={translate(opt.tkey)}
                selected={value === opt.key}
                onClick={() => setValue(opt.key)}
              />
              {value === "other" && opt.key === "other" && (
                <Box sx={{ mt: 2, pl: 1 }}>
                  <Input
                    label={translate("signup.right.discover.whereLabel")}
                    placeholder={translate("signup.right.discover.whereLabel")}
                    value={otherDetails}
                    onChange={(e) => setOtherDetails(e.target.value)}
                    data-cy="discover-other-input"
                    aria-label="where-other"
                    name="otherDetails"
                    sx={commonStyles.inputField}
                  />
                </Box>
              )}
              {value === "referral" && opt.key === "referral" && (
                <Box sx={{ mt: 2, pl: 1 }}>
                  <Input
                    label={translate("signup.right.discover.whoReferredLabel")}
                    placeholder={translate("signup.right.discover.whoReferredLabel")}
                    value={referralSource}
                    onChange={(e) => setReferralSource(e.target.value)}
                    data-cy="discover-referral-input"
                    aria-label="who-referred"
                    name="referralSource"
                    sx={commonStyles.inputField}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12} container spacing={2} mt={3}>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBack}
            disabled={isLoading}
            data-cy="backButton"
            aria-label="backButton"
            sx={commonStyles.backButton}
          >
            {translate("signup.right.back")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            fullWidth
            customColor={Color.SECONDARY_DARK}
            onClick={handleNext}
            disabled={!isValid || !!isLoading}
            data-cy="nextStepButton"
            sx={{
              height: 40,
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {translate("signup.right.next")}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default FifthStep;
